import React from 'react';
import {
    Box,
    FlexBox,
    FluidImage,
    GridContainer,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Row,
    Typography,
    responsive,
} from '@vp/swan';
import { NumberIcon } from '@99designs/feature-design-services-brief';
import { __ } from '@99designs/i18n';
import jobStep1 from '../../assets/images/job-step-1.png';
import jobStep2 from '../../assets/images/job-step-2.png';
import jobStep3 from '../../assets/images/job-step-3.png';

const ResponsiveModalDialogHeader = responsive(ModalDialogHeader);
const ResponsiveFlexBox = responsive(FlexBox);
const ResponsiveBox = responsive(Box);
const ResponsiveStepDescription = responsive(Typography);

export function HowItWorksModal({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) {
    return (
        <ModalDialog isOpen={isOpen} onRequestDismiss={() => setIsOpen(false)}>
            <ModalDialogContent aria-labelledby="modal dialog button">
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText={__('Close')} />
                </ModalDialogNav>
                <FlexBox flexDirection="column">
                    <ResponsiveModalDialogHeader xs={{ ml: 5 }} sm={{ ml: 0 }}>
                        <ModalDialogTitle>{__('How it works')}</ModalDialogTitle>
                    </ResponsiveModalDialogHeader>
                    <ModalDialogBody>
                        <HowItWorksStepCards />
                    </ModalDialogBody>
                </FlexBox>
            </ModalDialogContent>
        </ModalDialog>
    );
}

function HowItWorksStepCards() {
    const cardSteps = [
        {
            image: jobStep1,
            number: 1,
            title: __('Designers pitch for your project'),
            description: __(
                'Designers whose skills and preferences are a good fit for your project will now review your brief and let you know they are interested.'
            ),
        },
        {
            image: jobStep2,
            number: 2,
            title: __('Select your designer'),
            description: __(
                'As designers express interest in your project, they will appear here. You can view their portfolios, profiles, and reviews to help choose a designer to work with.'
            ),
        },
        {
            image: jobStep3,
            number: 3,
            title: __('Your project kicks off'),
            description: __(
                "Once you've selected a designer, the project will officially launch and they will message you with any questions or updates."
            ),
        },
    ];

    return (
        <GridContainer>
            <Row>
                <ResponsiveFlexBox flexDirection="column" justifyContent="space-between">
                    {cardSteps.map(({ image, number, title, description }) => (
                        <ResponsiveFlexBox
                            xs={{ flexDirection: 'column' }}
                            sm={{ flexDirection: 'row' }}
                            mb={number !== 3 ? 6 : undefined}
                            key={`image-step-${number}`}
                        >
                            <ResponsiveBox
                                xs={{ mx: 'auto' }}
                                style={{
                                    width: '173px',
                                    flex: '0 0 auto',
                                }}
                            >
                                <FluidImage src={image} alt={title} />
                            </ResponsiveBox>
                            <ResponsiveFlexBox ml={5}>
                                <FlexBox flexDirection="column">
                                    <FlexBox>
                                        <ResponsiveFlexBox
                                            xs={{ mt: 1 }}
                                            md={{ mt: 2 }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <NumberIcon num={number} />
                                        </ResponsiveFlexBox>
                                        <Typography fontSkin="title-item" mb={2} ml={2}>
                                            {title}
                                        </Typography>
                                    </FlexBox>
                                    <ResponsiveStepDescription
                                        textAlign={'left'}
                                        style={{ width: 'fit-content' }}
                                    >
                                        {description}
                                    </ResponsiveStepDescription>
                                </FlexBox>
                            </ResponsiveFlexBox>
                        </ResponsiveFlexBox>
                    ))}
                </ResponsiveFlexBox>
            </Row>
        </GridContainer>
    );
}
